import React, { useEffect, useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { Table, TableBody, TableHeader } from '../../../components/table';
import { Dialog } from '../../../components/ui';
import NoData from '../../../components/common/NoData';
import Loader from '../../../components/common/Loader';
import StatusBadge from '../../../components/ui/StatusBadge';
import { decodeData } from '../../../utils/encodeAndEncode';
import { singleSurveyDetails } from '../../../services/organization/courses/courseAnalytics.service';
import { useCourseName } from './CourseNameHeading';

function displayAnswer(value) {
  if (value === null || value === undefined || value === '') return 'No response';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function normalizeSurveyOptions(raw) {
  if (!raw) return [];
  let parsed = raw;
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return [];
    }
  }
  const options = Array.isArray(parsed)
    ? parsed
    : typeof parsed === 'object'
      ? Object.entries(parsed).map(([key, value]) => ({ option_key: key, content_value: value }))
      : [];
  return options.map((option, index) => {
    const value = option !== null && typeof option === 'object' ? option : { content_value: option };
    return {
      key: String(value.id ?? value.option_key ?? index),
      label: index < 26 ? String.fromCharCode(65 + index) : String(index + 1),
      text: String(value.text_fallback || value.content_value || value.option_value || value.text || 'Media option'),
      mediaUrl: value.media_url || null,
      contentType: value.content_type || 'text',
    };
  });
}

function CourseAnalyticsSurveyDetails() {
  const courseName = useCourseName();
  const location = useLocation();
  const navigate = useNavigate();
  const { surveyId, courseId } = useParams();
  const decodedSurveyId = surveyId ? decodeData(surveyId) : null;
  const decodedCourseId = courseId ? decodeData(courseId) : null;

  const [surveyDetails, setSurveyDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAllQuestions, setShowAllQuestions] = useState(false);
  const [selectedRespondent, setSelectedRespondent] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    setSurveyDetails(null);

    singleSurveyDetails({ assessment_id: decodedSurveyId, course_id: decodedCourseId })
      .then((response) => {
        if (!active) return;
        if (!response?.success) {
          setError(response?.message || 'Unable to load survey details.');
          return;
        }
        const raw = response.data;
        const questions = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
        setSurveyDetails({ questions, message: response.message });
      })
      .catch(() => {
        if (active) setError('Unable to load survey details. Please try again.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [decodedSurveyId, decodedCourseId]);

  const questions = surveyDetails?.questions || [];
  const respondents = useMemo(() => {
    const byUser = new Map();
    questions.forEach((question, questionIndex) => {
      (question.responses || []).forEach((response, responseIndex) => {
        const user = response.user || {};
        const key = user.id != null
          ? `id:${user.id}`
          : user.email ? `email:${user.email}` : `unknown:${questionIndex}:${responseIndex}`;
        if (!byUser.has(key)) {
          byUser.set(key, {
            key,
            name: user.name || 'Unknown trainee',
            email: user.email || 'No email',
            responses: [],
          });
        }
        byUser.get(key).responses.push({
          key: response.answer_id ?? `${questionIndex}-${responseIndex}`,
          question: question.question || `Question ${questionIndex + 1}`,
          answer: response.answer,
        });
      });
    });
    return Array.from(byUser.values());
  }, [questions]);

  if (loading) {
    return <Loader variant="section" caption="Loading survey analytics..." containerHeight="400px" />;
  }

  if (!surveyDetails) {
    return (
      <div className="tw-py-8">
        <NoData message={error || 'No survey details found'} />
        <div className="tw-text-center">
          <button type="button" className="ui-button ui-button-primary" onClick={() => navigate(`/admin/courses/analytics/${courseId}?tab=survey`)}>Back to surveys</button>
        </div>
      </div>
    );
  }

  const metrics = [
    { label: 'Total Questions', value: questions.length, icon: 'fluent:question-circle-24-regular', background: '#F4F7FE', iconColor: 'tw-text-indigo-600' },
    { label: 'Total Respondents', value: respondents.length, icon: 'fluent:people-24-regular', background: '#E6FAF5', iconColor: 'tw-text-emerald-600' },
  ];

  return (
    <>
      <div className="tw-mb-6 tw-w-full">
        <h4 className="tw-mb-0 tw-break-words">{courseName || 'Course'}</h4>
      </div>

      <div className="tw-mb-6 tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-2">
        {metrics.map((metric) => (
          <div key={metric.label} className="tw-min-w-0 tw-rounded-xl tw-border tw-border-slate-200 tw-p-5 tw-shadow-sm" style={{ backgroundColor: metric.background }}>
            <div className="tw-mb-2 tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-medium tw-text-slate-600">
              <Icon icon={metric.icon} width={22} height={22} className={metric.iconColor} />
              {metric.label}
            </div>
            <div className="tw-text-xl tw-font-semibold tw-text-slate-800">{metric.value}</div>
          </div>
        ))}
      </div>

      <section className="tw-mb-6 tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-p-5 tw-shadow-sm">
        <div className="tw-mb-4 tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-3">
          <div className="tw-min-w-0">
            <h5 className="tw-mb-1 tw-break-words tw-text-lg tw-font-semibold tw-text-slate-800">{location.state?.surveyName || 'Survey questions'}</h5>
            <p className="tw-mb-0 tw-text-sm tw-text-slate-500">Read-only preview of the questions in this survey.</p>
          </div>
          <StatusBadge tone="info">{questions.length} questions</StatusBadge>
        </div>

        {questions.length ? (
          <>
            <div className="tw-flex tw-flex-col tw-gap-3">
              {questions.slice(0, showAllQuestions ? questions.length : 2).map((question, index) => {
                const options = normalizeSurveyOptions(question.options);
                return <div key={question.question_id ?? index} className="tw-min-w-0 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-slate-50 tw-p-4">
                  <div className="tw-flex tw-items-start tw-gap-3">
                    <span className="tw-flex tw-h-7 tw-w-7 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-bg-indigo-100 tw-text-sm tw-font-semibold tw-text-indigo-700">{index + 1}</span>
                    <div className="tw-min-w-0 tw-flex-1">
                      <p className="tw-mb-2 tw-break-words tw-font-medium tw-text-slate-800">{question.question || 'Question text unavailable'}</p>
                      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                        <StatusBadge tone="neutral">{question.question_type || 'Survey question'}</StatusBadge>
                        <span className="tw-text-sm tw-text-slate-500">{question.responses?.length || 0} responses</span>
                      </div>
                      {options.length > 0 && (
                        <div className="tw-mt-3 tw-grid tw-grid-cols-1 tw-gap-2 sm:tw-grid-cols-2">
                          {options.map((option) => (
                            <div key={option.key} className="tw-flex tw-min-w-0 tw-items-start tw-gap-2 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-p-3">
                              <span className="tw-flex tw-h-6 tw-w-6 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-bg-slate-100 tw-text-xs tw-font-semibold tw-text-slate-600">{option.label}</span>
                              <div className="tw-min-w-0 tw-break-words tw-text-sm tw-text-slate-700">
                                {option.text}
                                {option.mediaUrl && (option.contentType === 'image' || option.contentType === 'image/*') && (
                                  <img src={option.mediaUrl} alt={option.text} className="tw-mt-2 tw-max-h-40 tw-max-w-full tw-rounded-md tw-object-contain" />
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>;
              })}
            </div>
            {questions.length > 2 && (
              <button type="button" className="tw-mt-4 tw-inline-flex tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-indigo-200 tw-bg-indigo-50 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-indigo-700 hover:tw-bg-indigo-100" onClick={() => setShowAllQuestions((value) => !value)} aria-expanded={showAllQuestions}>
                <Icon icon={showAllQuestions ? 'fluent:chevron-up-20-regular' : 'fluent:chevron-down-20-regular'} width={16} height={16} />
                {showAllQuestions ? 'Show fewer questions' : `Show all questions (${questions.length})`}
              </button>
            )}
          </>
        ) : (
          <NoData message="No questions found for this survey" description={surveyDetails.message || undefined} />
        )}
      </section>

      <section className="tw-overflow-hidden tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-shadow-sm">
        <div className="tw-p-5">
          <h5 className="tw-mb-1 tw-text-lg tw-font-semibold tw-text-slate-800">Survey responses</h5>
          <p className="tw-mb-0 tw-text-sm tw-text-slate-500">Select a trainee to review their recorded responses.</p>
        </div>
        {respondents.length ? (
          <div className="tw-w-full tw-overflow-x-auto">
            <Table className="tw-mb-0 tw-w-full tw-text-sm ui-table">
              <TableHeader>
                <tr className="tw-bg-slate-50 tw-text-left tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-600">
                  <th className="tw-whitespace-nowrap tw-px-5 tw-py-3">Trainee</th>
                  <th className="tw-whitespace-nowrap tw-px-5 tw-py-3">Responses</th>
                  <th className="tw-whitespace-nowrap tw-px-5 tw-py-3">Action</th>
                </tr>
              </TableHeader>
              <TableBody>
                {respondents.map((respondent) => (
                  <tr key={respondent.key} className="tw-border-t tw-border-slate-100 hover:tw-bg-slate-50">
                    <td className="tw-min-w-[220px] tw-px-5 tw-py-4">
                      <div className="tw-font-medium tw-text-slate-800">{respondent.name}</div>
                      <div className="tw-break-all tw-text-xs tw-text-slate-500">{respondent.email}</div>
                    </td>
                    <td className="tw-whitespace-nowrap tw-px-5 tw-py-4">{respondent.responses.length}</td>
                    <td className="tw-whitespace-nowrap tw-px-5 tw-py-4">
                      <button type="button" className="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-indigo-200 tw-bg-white tw-px-3 tw-py-2 tw-font-medium tw-text-indigo-700 hover:tw-bg-indigo-50" onClick={() => setSelectedRespondent(respondent)}>
                        <Icon icon="fluent:eye-20-regular" width={18} height={18} />
                        View responses
                      </button>
                    </td>
                  </tr>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <NoData message="No responses recorded for this survey" />
        )}
      </section>

      <Dialog show={Boolean(selectedRespondent)} onHide={() => setSelectedRespondent(null)} size="lg" scrollable>
        <Dialog.Header closeButton>
          <Dialog.Title>Responses from {selectedRespondent?.name || 'trainee'}</Dialog.Title>
        </Dialog.Header>
        <Dialog.Body>
          <div className="tw-flex tw-flex-col tw-gap-3">
            {selectedRespondent?.responses.map((response, index) => (
              <div key={`${response.key}-${index}`} className="tw-min-w-0 tw-rounded-lg tw-border tw-border-slate-200 tw-p-4">
                <div className="tw-mb-2 tw-break-words tw-font-medium tw-text-slate-800">{response.question}</div>
                <div className="tw-break-words tw-rounded-lg tw-bg-slate-50 tw-p-3 tw-text-sm tw-text-slate-700">{displayAnswer(response.answer)}</div>
              </div>
            ))}
          </div>
        </Dialog.Body>
        <Dialog.Footer>
          <button type="button" className="tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-4 tw-py-2 tw-font-medium tw-text-slate-700 hover:tw-bg-slate-50" onClick={() => setSelectedRespondent(null)}>Close</button>
        </Dialog.Footer>
      </Dialog>
    </>
  );
}

export default CourseAnalyticsSurveyDetails;
